/**
 * App Root — Impact Core
 *
 * Routing structure with ErrorBoundary wrapping and ThemeProvider.
 */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Shell } from '@/components/layout/Shell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoginPage } from '@/pages/LoginPage';
import { FundManagerDashboard } from '@/pages/dashboard/FundManagerDashboard';
import { InvesteeDashboard } from '@/pages/investee/InvesteeDashboard';
import { LPDashboard } from '@/pages/lp/LPDashboard';
import { SubmitDataPage } from '@/pages/investee/SubmitDataPage';
import { InvitationAcceptPage } from '@/pages/investee/InvitationAcceptPage';
import { FundsListPage } from '@/pages/funds/FundsListPage';
import { FundDetailPage } from '@/pages/funds/FundDetailPage';
import { InvesteesListPage } from '@/pages/investees/InvesteesListPage';
import { InvestmentsListPage } from '@/pages/investments/InvestmentsListPage';
import { InvestmentDetailPage } from '@/pages/investments/InvestmentDetailPage';
import { InvestmentPipelinePage } from '@/pages/investments/InvestmentPipelinePage';
import { ESGPage } from '@/pages/esg/ESGPage';
import { CompliancePage } from '@/pages/compliance/CompliancePage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { BIStudioPage } from '@/pages/bi/BIStudioPage';
import { AIAssistantPage } from '@/pages/ai/AIAssistantPage';
import { GeographicPage } from '@/pages/geo/GeographicPage';
import { BenchmarksPage } from '@/pages/benchmarks/BenchmarksPage';
import { KnowledgeGraphPage } from '@/pages/knowledge-graph/KnowledgeGraphPage';
import { CSRDPage } from '@/pages/csrd/CSRDPage';
import { RegulatoryPage } from '@/pages/regulatory/RegulatoryPage';
import { TasksPage } from '@/pages/tasks/TasksPage';
import { MessagesPage } from '@/pages/messages/MessagesPage';
import { UserManagementPage } from '@/pages/users/UserManagementPage';
import { MCPPage } from '@/pages/mcp/MCPPage';
import { TaxonomyPage } from '@/pages/taxonomy/TaxonomyPage';
import type { ReactNode } from 'react';

// ─── Query Client ──────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// ─── Protected Route ───────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-ic-surface">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-ic-accent border-t-transparent" />
          <span className="text-xs text-ic-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Shell>{children}</Shell>;
}

// ─── Page wrapper with ErrorBoundary ──────────────────────────

function Page({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <ErrorBoundary fallbackTitle={title ? `Error loading ${title}` : undefined}>
      {children}
    </ErrorBoundary>
  );
}

// ─── Placeholder Page ──────────────────────────────────────────

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-ic-text-primary">{title}</h2>
        <p className="mt-1 text-sm text-ic-text-tertiary">
          This section is under development.
        </p>
      </div>
    </div>
  );
}

// ─── Role-Adaptive Dashboard ──────────────────────────────────

function RoleAdaptiveDashboard() {
  const { icRole } = useAuth();

  if (icRole === 'investee_reporter') {
    return <InvesteeDashboard />;
  }

  if (icRole === 'lp_viewer') {
    return <LPDashboard />;
  }

  // fund_manager, org_admin, and all other roles use the FundManagerDashboard
  return <FundManagerDashboard />;
}

// ─── Router ────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitationAcceptPage />} />

      {/* Protected — Dashboard (role-adaptive) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Page title="Dashboard"><RoleAdaptiveDashboard /></Page>
          </ProtectedRoute>
        }
      />

      {/* Protected — Fund Manager Core Pages */}
      <Route path="/funds" element={<ProtectedRoute><Page title="Funds"><FundsListPage /></Page></ProtectedRoute>} />
      <Route path="/funds/:id" element={<ProtectedRoute><Page title="Fund Detail"><FundDetailPage /></Page></ProtectedRoute>} />
      <Route path="/investments" element={<ProtectedRoute><Page title="Investments"><InvestmentsListPage /></Page></ProtectedRoute>} />
      <Route path="/investments/:id" element={<ProtectedRoute><Page title="Investment Detail"><InvestmentDetailPage /></Page></ProtectedRoute>} />
      <Route path="/investees" element={<ProtectedRoute><Page title="Investees"><InvesteesListPage /></Page></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><Page title="Pipeline"><InvestmentPipelinePage /></Page></ProtectedRoute>} />
      <Route path="/esg" element={<ProtectedRoute><Page title="ESG"><ESGPage /></Page></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><Page title="Compliance"><CompliancePage /></Page></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Page title="Reports"><ReportsPage /></Page></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Page title="Settings"><SettingsPage /></Page></ProtectedRoute>} />

      {/* Protected — Org Admin */}
      <Route path="/users" element={<ProtectedRoute><Page title="Users"><UserManagementPage /></Page></ProtectedRoute>} />

      {/* Protected — Compliance Officer */}
      <Route path="/sfdr" element={<ProtectedRoute><Page title="SFDR"><CompliancePage /></Page></ProtectedRoute>} />
      <Route path="/pai" element={<ProtectedRoute><Page title="PAI"><CompliancePage /></Page></ProtectedRoute>} />
      <Route path="/taxonomy" element={<ProtectedRoute><Page title="Taxonomy"><TaxonomyPage /></Page></ProtectedRoute>} />
      <Route path="/filings" element={<ProtectedRoute><Page title="Filings"><CompliancePage /></Page></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Page title="Calendar"><ReportsPage /></Page></ProtectedRoute>} />

      {/* Protected — Intelligence Layer (Phase 3E) */}
      <Route path="/bi" element={<ProtectedRoute><Page title="BI Studio"><BIStudioPage /></Page></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><Page title="AI Assistant"><AIAssistantPage /></Page></ProtectedRoute>} />
      <Route path="/geographic" element={<ProtectedRoute><Page title="Geographic"><GeographicPage /></Page></ProtectedRoute>} />

      {/* Protected — Phase 4 Ecosystem Pages */}
      <Route path="/knowledge-graph" element={<ProtectedRoute><Page title="Knowledge Graph"><KnowledgeGraphPage /></Page></ProtectedRoute>} />
      <Route path="/csrd" element={<ProtectedRoute><Page title="CSRD"><CSRDPage /></Page></ProtectedRoute>} />
      <Route path="/regulatory" element={<ProtectedRoute><Page title="Regulatory"><RegulatoryPage /></Page></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><Page title="Tasks"><TasksPage /></Page></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Page title="Messages"><MessagesPage /></Page></ProtectedRoute>} />
      <Route path="/mcp" element={<ProtectedRoute><Page title="MCP"><MCPPage /></Page></ProtectedRoute>} />

      {/* Protected — Investee Reporter */}
      <Route path="/submit" element={<ProtectedRoute><Page title="Submit Data"><SubmitDataPage /></Page></ProtectedRoute>} />
      <Route path="/submissions" element={<ProtectedRoute><Page title="Submissions"><ESGPage /></Page></ProtectedRoute>} />
      <Route path="/benchmarks" element={<ProtectedRoute><Page title="Benchmarks"><BenchmarksPage /></Page></ProtectedRoute>} />

      {/* Protected — LP Viewer */}
      <Route path="/portfolio" element={<ProtectedRoute><Page title="Portfolio"><LPDashboard /></Page></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute><Page title="Performance"><LPDashboard /></Page></ProtectedRoute>} />
      <Route path="/esg-summary" element={<ProtectedRoute><Page title="ESG Summary"><LPDashboard /></Page></ProtectedRoute>} />

      {/* Protected — Investment Officer */}
      <Route path="/due-diligence" element={<ProtectedRoute><Page title="Due Diligence"><PlaceholderPage title="Due Diligence" /></Page></ProtectedRoute>} />

      {/* Protected — Impact Officer */}
      <Route path="/impact" element={<ProtectedRoute><Page title="Impact"><PlaceholderPage title="Impact Metrics" /></Page></ProtectedRoute>} />
      <Route path="/sdg" element={<ProtectedRoute><Page title="SDG"><PlaceholderPage title="SDG Mapping" /></Page></ProtectedRoute>} />

      {/* Protected — Auditor */}
      <Route path="/audit" element={<ProtectedRoute><Page title="Audit"><PlaceholderPage title="Audit Trail" /></Page></ProtectedRoute>} />
      <Route path="/snapshots" element={<ProtectedRoute><Page title="Snapshots"><PlaceholderPage title="Data Snapshots" /></Page></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ─── App ───────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
